import React, { useMemo, useState, useEffect } from "react";
import { toast } from "react-toastify";
import useSWR, { mutate } from "swr";
import {
    AppConstants,
    AppRouteUi,
    PlatformApi,
    topLoaderProgress,
} from "../../configs";
import { AxiosAuth } from "../../core";
import {
    ApiResponse,
    ApiSchemaVendorSipHeaderModel,
    WithPagination,
} from "../../models";
import { UtilsJQuery } from "../../utils";

type SipHeaderHookProps = {
    setAddModalShow?: (value: boolean) => void;
    setDeleteModalShow?: (value: boolean) => void;
    setEditModalShow?: (value: boolean) => void;
    shouldVendorSipHeaderGetAllInvoke?: boolean;
    pageSize?: number;
    pageNo?: number;
    searchText?: string;
};

export function useFunctionalityVendorSipHeader(props: SipHeaderHookProps) {
    const {
        setAddModalShow,
        setDeleteModalShow,
        setEditModalShow,
        shouldVendorSipHeaderGetAllInvoke = false,
        pageSize,
        pageNo,
        searchText,
    } = props;

    const vendorId = AppRouteUi.Vendors.Profile.useParam();


    const getAllSipHeadersUrl = React.useMemo(() => {
        if (!vendorId) return null;
        return PlatformApi.Vendors.SIP.GetVendorSipHeaderListById(
            vendorId,
            pageSize ?? 10,
            pageNo ?? 1,
            (searchText?.trim() || undefined)
        );
    }, [vendorId, pageSize, pageNo, searchText]);

    const { data: apiVendorSipHeaderList, error } = useSWR<ApiSchemaVendorSipHeaderModel[]>(
        shouldVendorSipHeaderGetAllInvoke && getAllSipHeadersUrl ? getAllSipHeadersUrl : null,
        async (url: string) => {
            const r = await AxiosAuth.get(url);
            if (Array.isArray(r.data?.data?.data)) return r.data.data.data as ApiSchemaVendorSipHeaderModel[];

            throw new Error(`Unexpected response shape: ${JSON.stringify(r.data)}`);
        },
        { revalidateOnFocus: false, shouldRetryOnError: false }
    );

    const vendorSipHeaderList = React.useMemo(
        () => (Array.isArray(apiVendorSipHeaderList) ? apiVendorSipHeaderList : []),
        [apiVendorSipHeaderList]
    );

    useEffect(() => {
        if (error) console.error('[SIP Headers][SWR error]', error);
    }, [error]);


    const onAddSipHeader = React.useCallback(
        async (form: Pick<ApiSchemaVendorSipHeaderModel, "headerName" | "headerValue">) => {
            const btnLoading = UtilsJQuery.Ladda(".login-from-submit-btn");
            btnLoading.start?.();

            const formToSubmit = {
                vendorId,
                headerName: form.headerName,
                headerValue: form.headerValue,
            };

            try {
                const res = await AxiosAuth.post(
                    PlatformApi.Vendors.SIP.CreateVendorSIPHeader(),
                    formToSubmit
                );

                if (res.status === AppConstants.Api.StatusOK) {
                    mutate(getAllSipHeadersUrl);
                    toast.success("SIP header added successfully");
                    setAddModalShow?.(false);
                }
            } catch (e) {
                console.error(e);
                toast.error("Adding SIP header failed");
            }

            btnLoading.stop?.();
            return Promise.resolve(null);
        },
        [vendorId, getAllSipHeadersUrl, setAddModalShow]
    );

    const onUpdateSipHeader = React.useCallback(
        async (id: string, form: Pick<ApiSchemaVendorSipHeaderModel, "headerName" | "headerValue">) => {
            const btnLoading = UtilsJQuery.Ladda(".login-from-submit-btn");
            btnLoading.start?.();

            const formToSubmit = {
                vendorId,
                headerName: form.headerName,
                headerValue: form.headerValue,
            };

            try {
                const res = await AxiosAuth.put(
                    PlatformApi.Vendors.SIP.UpdateVendorSipHeader(id),
                    formToSubmit
                );

                if (res.status === AppConstants.Api.StatusOK) {
                    mutate(getAllSipHeadersUrl);
                    toast.success("SIP header updated successfully");
                    setEditModalShow?.(false);
                }
            } catch (e) {
                console.error(e);
                toast.error("Updating SIP header failed");
            }

            btnLoading.stop?.();
            return Promise.resolve(null);
        },
        [vendorId, getAllSipHeadersUrl, setEditModalShow]
    );

    const onDeleteSipHeader = React.useCallback(
        async (id: string) => {
            const btnLoading = UtilsJQuery.Ladda(".user-from-submit-btn");
            btnLoading.start?.();

            try {
                const res = await AxiosAuth.delete(
                    PlatformApi.Vendors.SIP.DeleteVendorSipHeader(id)
                );

                if (res.status === AppConstants.Api.StatusOK) {
                    mutate(getAllSipHeadersUrl);
                    toast.success("SIP header deleted successfully");
                    setDeleteModalShow?.(false);
                }
            } catch (e) {
                console.error(e);
                toast.error("Deleting SIP header failed");
            }

            btnLoading.stop?.();
            return Promise.resolve(null);
        },
        [getAllSipHeadersUrl, setDeleteModalShow]
    );

    return {
        vendorSipHeaderList,
        getAllSipHeadersUrl,
        onAddSipHeader,
        onUpdateSipHeader,
        onDeleteSipHeader
    };
}
